import React, { useEffect, useState, useCallback, useRef } from "react";
import {
    Box, Typography, IconButton, Modal, Fade, Backdrop,
    Button, Stack, CircularProgress, Pagination, TextField,
    InputAdornment, ToggleButton, ToggleButtonGroup,
    MenuItem, Select, FormControl, InputLabel,
    Collapse, Skeleton, Alert, Snackbar
} from "@mui/material";
import {
    Close as CloseIcon, CalendarToday as CalendarIcon,
    Person as PersonIcon, Phone as PhoneIcon,
    Email as EmailIcon,
    FilterList as FilterIcon, Clear as ClearIcon,
    LocationOn as LocationIcon, Home as HomeIcon,
    Refresh as RefreshIcon, AttachMoney as PriceIcon,
    SquareFoot as SqftIcon
} from "@mui/icons-material";
import { HiOutlineClipboardList } from "react-icons/hi";
import { MdOutlineEventNote } from "react-icons/md";
import { vendorApi } from "../../api/api";

// ─── Constants ────────────────────────────────────────────────────────────────
// ✅ Match your DB: capitalized status values
const STATUS_MAP = {
    Completed: { color: "#00897b", bg: "#e0f2f1", dot: "#26a69a" },
    Pending: { color: "#f57c00", bg: "#fff3e0", dot: "#ffa726" },
    Cancelled: { color: "#c62828", bg: "#ffebee", dot: "#ef5350" },
    Scheduled: { color: "#1565c0", bg: "#e3f2fd", dot: "#42a5f5" },
    default: { color: "#546e7a", bg: "#eceff1", dot: "#90a4ae" },
};

// ✅ Capitalized to match DB values
const ALL_STATUSES = ["all", "Pending", "Scheduled", "Completed", "Cancelled"];
const STATUS_OPTIONS = ["Pending", "Scheduled", "Completed", "Cancelled"];

const SORT_OPTIONS = [
    { value: "newest", label: "Newest First" },
    { value: "oldest", label: "Oldest First" },
    { value: "name_asc", label: "Name A → Z" },
    { value: "name_desc", label: "Name Z → A" },
];

const DEFAULT_FILTERS = {
    status: "all", sort: "newest", date_from: "", date_to: "",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getStatus = (s) => STATUS_MAP[s] ?? STATUS_MAP.default;



const fmt = (d) => {
    if (!d) return "N/A";
    const date = new Date(d);
    if (isNaN(date)) return d; // fallback for any unconverted varchar
    return date.toLocaleString(undefined, {
        weekday: "short", year: "numeric", month: "short",
        day: "numeric", hour: "2-digit", minute: "2-digit",
    });
};

const fmtHeader = (d) =>
    new Date(d).toLocaleDateString(undefined, {
        weekday: "long", year: "numeric", month: "long", day: "numeric",
    });

const formatPrice = (p) =>
    p ? `₹${Number(p).toLocaleString("en-IN")}` : null;

// ─── StatusBadge ──────────────────────────────────────────────────────────────
const StatusBadge = ({ status, size = "sm" }) => {
    const s = getStatus(status);
    return (
        <Box display="inline-flex" alignItems="center" gap={0.6} sx={{
            px: size === "sm" ? 1.2 : 1.8,
            py: size === "sm" ? 0.3 : 0.5,
            borderRadius: "20px",
            backgroundColor: s.bg,
            border: `1px solid ${s.dot}40`,
        }}>
            <Box sx={{
                width: size === "sm" ? 7 : 9,
                height: size === "sm" ? 7 : 9,
                borderRadius: "50%",
                backgroundColor: s.dot,
                flexShrink: 0,
                animation: status === "Pending"
                    ? "blink 1.8s ease-in-out infinite" : "none",
                "@keyframes blink": {
                    "0%,100%": { opacity: 1 },
                    "50%": { opacity: 0.2 },
                },
            }} />
            <Typography sx={{
                fontSize: size === "sm" ? "11px" : "13px",
                fontWeight: 600, color: s.color,
                letterSpacing: "0.3px",
            }}>
                {status || "Unknown"}
            </Typography>
        </Box>
    );
};

// ─── CardSkeleton ─────────────────────────────────────────────────────────────
const CardSkeleton = () => (
    <Box sx={{
        width: { xs: "100%", sm: "calc(50% - 8px)", md: "calc(33.33% - 11px)" },
        borderRadius: "16px", border: "1.5px solid #f0f0f0",
        backgroundColor: "#fff", p: 2.5,
    }}>
        <Box display="flex" justifyContent="space-between" mb={1.5}>
            <Skeleton variant="rounded" width={90} height={26} sx={{ borderRadius: "20px" }} />
            <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: "8px" }} />
        </Box>
        <Skeleton variant="text" width="70%" height={22} />
        <Skeleton variant="text" width="40%" height={16} />
        <Skeleton variant="text" width="55%" height={16} />
        <Skeleton variant="text" width="40%" height={16} sx={{ mb: 1.5 }} />
        <Skeleton variant="text" width="85%" height={14} />
    </Box>
);

// ─── VisitCard ────────────────────────────────────────────────────────────────
const VisitCard = React.memo(({ visit, onClick }) => {
    const propertyName = visit.PropertyName || visit.ApartmentName || "Property";
    const city = visit.property_city || visit.apartment_city;

    return (
        <Box onClick={() => onClick(visit)} sx={{
            width: { xs: "100%", sm: "calc(50% - 8px)", md: "calc(33.33% - 11px)" },
            cursor: "pointer", borderRadius: "16px",
            border: "1.5px solid #f0f0f0", backgroundColor: "#fff",
            p: 2.5, transition: "all 0.22s cubic-bezier(0.34,1.56,0.64,1)",
            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: "0 12px 28px rgba(0,0,0,0.10)",
                borderColor: "#c8d8f0",
            },
        }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                <StatusBadge status={visit.status} />
                <Box sx={{
                    width: 32, height: 32, borderRadius: "8px",
                    backgroundColor: "#f5f5f5", display: "flex",
                    alignItems: "center", justifyContent: "center",
                }}>
                    <LocationIcon sx={{ fontSize: 16, color: "#888" }} />
                </Box>
            </Box>

            <Typography fontWeight={700} fontSize="15px" color="#1a1a2e" sx={{
                mb: 0.4, lineHeight: 1.3, display: "-webkit-box",
                WebkitLineClamp: 1, WebkitBoxOrient: "vertical", overflow: "hidden",
            }}>
                {propertyName}
            </Typography>

            {city && (
                <Typography fontSize="11.5px" color="#aaa" sx={{ mb: 0.5 }}>
                    📍 {city}
                </Typography>
            )}

            <Typography fontSize="13px" color="#555" fontWeight={500} sx={{ mb: 0.3 }}>
                {visit.customer_name}
            </Typography>

            <Typography fontSize="12px" color="#888" sx={{ mb: 1 }}>
                📞 {visit.Mobilenumber}
            </Typography>

            {visit.FlatName && (
                <Box display="flex" alignItems="center" gap={0.8} mb={1} sx={{
                    px: 1, py: 0.5, borderRadius: "6px",
                    backgroundColor: "#f8f9fc", width: "fit-content",
                }}>
                    <HomeIcon sx={{ fontSize: 12, color: "#aaa" }} />
                    <Typography fontSize="11.5px" color="#777" fontWeight={500}>
                        {visit.FlatName}
                        {visit.FloorNo ? ` · Floor ${visit.FloorNo}` : ""}
                        {visit.flat_price ? ` · ${formatPrice(visit.flat_price)}` : ""}
                    </Typography>
                </Box>
            )}

            <Box sx={{
                pt: 1.5, borderTop: "1px dashed #f0f0f0",
                display: "flex", alignItems: "center", gap: 0.8,
            }}>
                <CalendarIcon sx={{ fontSize: 13, color: "#bbb" }} />
                <Typography fontSize="11.5px" color="#aaa">{fmt(visit.visit_datetime)}</Typography>
            </Box>
        </Box>
    );
});

// ─── DetailRow ────────────────────────────────────────────────────────────────
const DetailRow = ({ icon: Icon, label, value, highlight }) =>
    value ? (
        <Box sx={{
            display: "flex", alignItems: "flex-start", gap: 1.5,
            p: 1.5, borderRadius: "10px",
            backgroundColor: highlight ? "#eff6ff" : "#fafafa",
            border: `1px solid ${highlight ? "#dbeafe" : "#f0f0f0"}`,
        }}>
            <Box sx={{
                width: 34, height: 34, borderRadius: "8px",
                backgroundColor: highlight ? "#dbeafe" : "#eff6ff",
                display: "flex", alignItems: "center",
                justifyContent: "center", flexShrink: 0,
            }}>
                <Icon sx={{ fontSize: 17, color: "#1565c0" }} />
            </Box>
            <Box>
                <Typography fontSize="11px" color="#999" fontWeight={500}
                    letterSpacing="0.4px" textTransform="uppercase" mb={0.2}>
                    {label}
                </Typography>
                <Typography fontSize="14px" color="#1a1a2e" fontWeight={500}>
                    {value}
                </Typography>
            </Box>
        </Box>
    ) : null;

// ─── Main Component ───────────────────────────────────────────────────────────
const VendorSiteVisits = () => {
    const [visits, setVisits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [selectedVisit, setSelectedVisit] = useState(null);
    const [page, setPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [counts, setCounts] = useState({});
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [showFilters, setShowFilters] = useState(false);
    const [updatingStatus, setUpdatingStatus] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false, message: "", severity: "success",
    });
    const abortRef = useRef(null);

    const setFilter = useCallback((key, value) => {
        setFilters((p) => ({ ...p, [key]: value }));
        if (key !== "search") setPage(1);
    }, []);

    const clearFilters = useCallback(() => {
        setFilters(DEFAULT_FILTERS);
        setPage(1);
    }, []);

    const hasActiveFilters =
    filters.status !== "all" ||
    filters.date_from        ||
    filters.date_to          ||
    filters.sort !== "newest";

    // ─── Fetch ────────────────────────────────────────────────────────────────
    const fetchVisits = useCallback(async (isRefresh = false) => {
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();

        try {
            isRefresh ? setRefreshing(true) : setLoading(true);
            setError(null);

            const params = {
                page,
                per_page: 15,
                sort: filters.sort,
                ...(filters.status !== "all" && { status: filters.status }),
                ...(filters.date_from && { date_from: filters.date_from }),
                ...(filters.date_to && { date_to: filters.date_to }),
            };

            const res = await vendorApi.getVendorSiteVisits(params);
            const d = res.data.data;

            setVisits(d.data || []);
            setLastPage(d.last_page || 1);
            setTotal(d.total || 0);
            setCounts(res.data.counts || {});
        } catch (err) {
            if (err.name !== "CanceledError") {
                setError("Failed to load site visits. Please try again.");
            }
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [page, filters.status, filters.sort, filters.date_from, filters.date_to]);

    useEffect(() => { fetchVisits(); }, [fetchVisits]);



    // ─── Status update ────────────────────────────────────────────────────────
    // const handleStatusUpdate = async (visitId, newStatus) => {
    //     try {
    //         setUpdatingStatus(true);
    //         await vendorApi.updateVisitStatus(visitId, newStatus);

    //         setVisits((prev) =>
    //             prev.map((v) => v.id === visitId ? { ...v, status: newStatus } : v)
    //         );
    //         if (selectedVisit?.id === visitId) {
    //             setSelectedVisit((p) => ({ ...p, status: newStatus }));
    //         }

    //         setSnackbar({ open: true, message: "Status updated!", severity: "success" });
    //     } catch(error) {
    //         console.log(error);
    //         setSnackbar({ open: true, message: "Failed to update status.", severity: "error" });
    //     } finally {
    //         setUpdatingStatus(false);
    //     }
    // };

    // ─── Group by date ────────────────────────────────────────────────────────
    const grouped = visits.reduce((acc, v) => {
        if (!v.visit_datetime) return acc;
        const key = new Date(v.visit_datetime).toISOString().split("T")[0];
        (acc[key] = acc[key] || []).push(v);
        return acc;
    }, {});
    const sortedDates = Object.keys(grouped).sort((a, b) =>
        filters.sort === "oldest" ? new Date(a) - new Date(b) : new Date(b) - new Date(a)
    );

    return (
        <Box sx={{ minHeight: "100vh", backgroundColor: "#f8f9fc", p: { xs: 2, md: 4 } }}>

            {/* ── Header ── */}
            <Box display="flex" justifyContent="space-between"
                alignItems="center" mb={3} flexWrap="wrap" gap={2}>
                <Box>
                    <Typography fontWeight={800} fontSize={{ xs: "20px", md: "24px" }}
                        color="#1a1a2e" letterSpacing="-0.5px">
                        Site Visit Requests
                    </Typography>
                    <Typography fontSize="13px" color="#999" mt={0.3}>
                        {counts.total ?? total} total visits
                        {hasActiveFilters && (
                            <> · <span style={{ color: "#1565c0", fontWeight: 600 }}>
                                {total} filtered
                            </span></>
                        )}
                    </Typography>
                </Box>

                <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
                    <Box sx={{
                        px: 2, py: 1, borderRadius: "10px",
                        backgroundColor: "#eff6ff", border: "1px solid #dbeafe",
                        display: "flex", alignItems: "center", gap: 1,
                    }}>
                        <MdOutlineEventNote size={18} color="#1565c0" />
                        <Typography fontSize="13px" fontWeight={600} color="#1565c0">
                            {total} visits
                        </Typography>
                    </Box>

                    <IconButton onClick={() => fetchVisits(true)} disabled={refreshing}
                        sx={{ border: "1px solid #eee", borderRadius: "10px", width: 36, height: 36 }}>
                        {refreshing
                            ? <CircularProgress size={16} />
                            : <RefreshIcon sx={{ fontSize: 18, color: "#888" }} />}
                    </IconButton>

                    <Button
                        startIcon={<FilterIcon />}
                        onClick={() => setShowFilters((p) => !p)}
                        variant={showFilters ? "contained" : "outlined"}
                        size="small"
                        sx={{
                            borderRadius: "10px", textTransform: "none",
                            fontWeight: 600, fontSize: "13px",
                            ...(showFilters
                                ? { backgroundColor: "#1565c0", boxShadow: "none" }
                                : { borderColor: "#ddd", color: "#555" }),
                        }}
                    >
                        Filters
                        {hasActiveFilters && (
                            <Box component="span" sx={{
                                ml: 0.8, width: 8, height: 8, borderRadius: "50%",
                                backgroundColor: showFilters ? "rgba(255,255,255,0.8)" : "#ef5350",
                                display: "inline-block", flexShrink: 0,
                            }} />
                        )}
                    </Button>
                </Box>
            </Box>

            {/* ── Error ── */}
            {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: "12px" }}
                    action={
                        <Button color="inherit" size="small" onClick={() => fetchVisits()}>
                            Retry
                        </Button>
                    }>
                    {error}
                </Alert>
            )}

            {/* ── Filter Panel ── */}
            <Collapse in={showFilters}>
                <Box sx={{
                    mb: 3, p: 3, borderRadius: "16px",
                    backgroundColor: "#fff", border: "1.5px solid #e8edf5",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                }}>
                    {/* Search + Sort */}
                    <Box display="flex" gap={2} flexWrap="wrap" mb={2.5}>

                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel sx={{ fontSize: "13px" }}>Sort By</InputLabel>
                            <Select
                                value={filters.sort} label="Sort By"
                                onChange={(e) => setFilter("sort", e.target.value)}
                                sx={{ borderRadius: "10px", fontSize: "13px" }}
                            >
                                {SORT_OPTIONS.map((o) => (
                                    <MenuItem key={o.value} value={o.value}
                                        sx={{ fontSize: "13px" }}>
                                        {o.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Status Tabs with counts from API */}
                    <Box mb={2.5}>
                        <Typography fontSize="11px" color="#999" fontWeight={600}
                            letterSpacing="0.5px" textTransform="uppercase" mb={1}>
                            Status
                        </Typography>
                        <ToggleButtonGroup
                            value={filters.status} exclusive
                            onChange={(_, v) => v && setFilter("status", v)}
                            sx={{
                                flexWrap: "wrap", gap: 1,
                                "& .MuiToggleButtonGroup-grouped": {
                                    border: "none !important", margin: 0,
                                },
                            }}
                        >
                            {ALL_STATUSES.map((s) => {
                                const st = s === "all" ? null : getStatus(s);
                                const isActive = filters.status === s;
                                const count = s === "all"
                                    ? (counts.total ?? 0)
                                    : (counts[s] ?? 0);

                                return (
                                    <ToggleButton key={s} value={s} sx={{
                                        px: 1.8, py: 0.6,
                                        borderRadius: "20px !important",
                                        fontSize: "12px", fontWeight: 600,
                                        border: "1.5px solid #eee !important",
                                        backgroundColor: isActive
                                            ? (s === "all" ? "#1565c0" : st?.bg) : "#fafafa",
                                        color: isActive
                                            ? (s === "all" ? "#fff" : st?.color) : "#aaa",
                                        transition: "all 0.15s ease",
                                        gap: 0.8,
                                        "&:hover": {
                                            backgroundColor: s === "all" ? "#1976d2" : st?.bg,
                                            color: s === "all" ? "#fff" : st?.color,
                                        },
                                    }}>
                                        {s === "all" ? "All" : s}
                                        <Box component="span" sx={{
                                            px: 0.8, py: 0.1, borderRadius: "10px",
                                            backgroundColor: isActive
                                                ? "rgba(255,255,255,0.25)" : "#ebebeb",
                                            color: isActive
                                                ? (s === "all" ? "#fff" : st?.color) : "#999",
                                            fontSize: "10px", fontWeight: 700,
                                        }}>
                                            {count}
                                        </Box>
                                    </ToggleButton>
                                );
                            })}
                        </ToggleButtonGroup>
                    </Box>

                    {/* Date Range + Clear */}
                    <Box display="flex" gap={2} flexWrap="wrap" alignItems="flex-end">
                        <Box>
                            <Typography fontSize="11px" color="#999" fontWeight={600}
                                letterSpacing="0.5px" textTransform="uppercase" mb={1}>
                                Date Range
                            </Typography>
                            <Box display="flex" gap={1.5} flexWrap="wrap">
                                <TextField
                                    type="date" size="small" label="From"
                                    value={filters.date_from}
                                    onChange={(e) => setFilter("date_from", e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{
                                        width: 160,
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "10px", fontSize: "13px",
                                        },
                                    }}
                                />
                                <TextField
                                    type="date" size="small" label="To"
                                    value={filters.date_to}
                                    onChange={(e) => setFilter("date_to", e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{
                                        width: 160,
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "10px", fontSize: "13px",
                                        },
                                    }}
                                />
                            </Box>
                        </Box>

                        {hasActiveFilters && (
                            <Button onClick={clearFilters} size="small"
                                startIcon={<ClearIcon />} variant="outlined"
                                sx={{
                                    borderRadius: "10px", textTransform: "none",
                                    fontSize: "12px", fontWeight: 600,
                                    color: "#ef5350", borderColor: "#ffcdd2",
                                    "&:hover": {
                                        backgroundColor: "#ffebee",
                                        borderColor: "#ef9a9a",
                                    },
                                }}>
                                Clear All
                            </Button>
                        )}
                    </Box>
                </Box>
            </Collapse>

            {/* ── Loading ── */}
            {loading ? (
                <Box>
                    {[0, 1].map((g) => (
                        <Box key={g} mb={4}>
                            <Skeleton variant="text" width={240} height={28} sx={{ mb: 2 }} />
                            <Box display="flex" flexWrap="wrap" gap={2}>
                                {[0, 1, 2].map((i) => <CardSkeleton key={i} />)}
                            </Box>
                        </Box>
                    ))}
                </Box>

            ) : visits.length === 0 ? (
                <Box display="flex" flexDirection="column" alignItems="center"
                    justifyContent="center" sx={{ minHeight: "40vh", gap: 2 }}>
                    <Box sx={{
                        width: 80, height: 80, borderRadius: "20px",
                        backgroundColor: "#f0f4f8", display: "flex",
                        alignItems: "center", justifyContent: "center",
                    }}>
                        <HiOutlineClipboardList size={36} color="#c5d0dc" />
                    </Box>
                    <Typography fontWeight={700} color="#aaa" fontSize="15px">
                        {hasActiveFilters ? "No results match your filters" : "No site visits yet"}
                    </Typography>
                    <Typography fontSize="13px" color="#ccc">
                        {hasActiveFilters
                            ? "Try adjusting or clearing your filters"
                            : "New visit requests will appear here"}
                    </Typography>
                    {hasActiveFilters && (
                        <Button onClick={clearFilters} size="small" variant="outlined"
                            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600 }}>
                            Clear Filters
                        </Button>
                    )}
                </Box>

            ) : (
                <>
                    {sortedDates.map((dateKey) => (
                        <Box key={dateKey} mb={4}>
                            <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                                <Box sx={{
                                    px: 1.5, py: 0.4, borderRadius: "8px",
                                    backgroundColor: "#1565c0", color: "#fff",
                                    fontSize: "12px", fontWeight: 700,
                                }}>
                                    {grouped[dateKey].length}
                                </Box>
                                <Typography fontWeight={700} fontSize="15px" color="#1a1a2e">
                                    {fmtHeader(dateKey)}
                                </Typography>
                                <Box sx={{ flex: 1, height: "1px", backgroundColor: "#ebebeb" }} />
                            </Box>
                            <Box display="flex" flexWrap="wrap" gap={2}>
                                {grouped[dateKey].map((visit) => (
                                    <VisitCard key={visit.id} visit={visit}
                                        onClick={setSelectedVisit} />
                                ))}
                            </Box>
                        </Box>
                    ))}

                    <Box display="flex" justifyContent="space-between" alignItems="center"
                        mt={4} flexWrap="wrap" gap={2}
                        sx={{ pt: 3, borderTop: "1px solid #eee" }}>
                        <Typography fontSize="13px" color="#aaa">
                            Showing{" "}
                            <strong style={{ color: "#555" }}>{visits.length}</strong> of{" "}
                            <strong style={{ color: "#555" }}>{total}</strong> visits
                        </Typography>
                        <Pagination
                            count={lastPage} page={page}
                            onChange={(_, v) => { setPage(v); window.scrollTo(0, 0); }}
                            color="primary" shape="rounded"
                            showFirstButton showLastButton disabled={lastPage <= 1}
                            sx={{
                                "& .MuiPaginationItem-root": {
                                    borderRadius: "8px", fontWeight: 600,
                                },
                                "& .Mui-selected": {
                                    backgroundColor: "#1565c0 !important",
                                },
                            }}
                        />
                    </Box>
                </>
            )}

            {/* ── Detail Modal ── */}
            <Modal open={!!selectedVisit} onClose={() => setSelectedVisit(null)}
                closeAfterTransition BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 400,
                    sx: { backdropFilter: "blur(5px)", backgroundColor: "rgba(0,0,0,0.35)" },
                }}>
                <Fade in={!!selectedVisit}>
                    <Box sx={{
                        position: "absolute", top: "50%", left: "50%",
                        transform: "translate(-50%,-50%)",
                        width: { xs: "92%", sm: "540px" },
                        maxHeight: "90vh", overflow: "auto",
                        backgroundColor: "#fff", borderRadius: "20px",
                        boxShadow: "0 40px 100px rgba(0,0,0,0.20)", outline: "none",
                    }}>
                        {selectedVisit && (
                            <>
                                {/* Header */}
                                <Box sx={{
                                    p: 3, pb: 2, borderBottom: "1px solid #f0f0f0",
                                    display: "flex", justifyContent: "space-between",
                                    alignItems: "flex-start",
                                }}>
                                    <Box flex={1} pr={2}>
                                        <Typography fontWeight={800} fontSize="18px"
                                            color="#1a1a2e" lineHeight={1.3} mb={0.3}>
                                            {selectedVisit.PropertyName ||
                                                selectedVisit.ApartmentName || "Property"}
                                        </Typography>
                                        {(selectedVisit.property_city || selectedVisit.apartment_city) && (
                                            <Typography fontSize="12.5px" color="#aaa" mb={1}>
                                                📍 {selectedVisit.property_city || selectedVisit.apartment_city}
                                            </Typography>
                                        )}
                                        <StatusBadge status={selectedVisit.status} size="lg" />
                                    </Box>
                                    <IconButton onClick={() => setSelectedVisit(null)}
                                        size="small" sx={{
                                            backgroundColor: "#f5f5f5", borderRadius: "8px",
                                            "&:hover": { backgroundColor: "#eee" },
                                        }}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Box>

                                {/* Body */}
                                <Box p={3}>
                                    <Stack spacing={1.5}>
                                        <DetailRow icon={PersonIcon} label="Customer"
                                            value={selectedVisit.customer_name} highlight />
                                        <DetailRow icon={PhoneIcon} label="Mobile"
                                            value={selectedVisit.Mobilenumber} />
                                        <DetailRow icon={EmailIcon} label="Email"
                                            value={selectedVisit.Email} />
                                        <DetailRow icon={LocationIcon} label="Address"
                                            value={
                                                selectedVisit.property_address ||
                                                selectedVisit.apartment_address
                                            }
                                        />
                                        <DetailRow icon={HomeIcon} label="Flat"
                                            value={
                                                selectedVisit.FlatName
                                                    ? `${selectedVisit.FlatName}${selectedVisit.FloorNo ? ` · Floor ${selectedVisit.FloorNo}` : ""}  ${selectedVisit.FlatType ? `(${selectedVisit.FlatType})` : ""}`
                                                    : null
                                            }
                                        />
                                        <DetailRow icon={SqftIcon} label="Size"
                                            value={selectedVisit.SizeInSqFt
                                                ? `${selectedVisit.SizeInSqFt} sq.ft`
                                                : null}
                                        />
                                        <DetailRow icon={PriceIcon} label="Price"
                                            value={formatPrice(selectedVisit.flat_price)} />
                                        <DetailRow icon={CalendarIcon} label="Visit Date & Time"
                                            value={fmt(selectedVisit.visit_datetime)} />
                                    </Stack>

                                    {/* Status Update */}
                                    {/* <Box mt={3} p={2} sx={{
                                        borderRadius: "12px",
                                        backgroundColor: "#f8f9fc",
                                        border: "1px solid #eee",
                                    }}>
                                        <Typography fontSize="11px" color="#999" fontWeight={600}
                                            letterSpacing="0.5px" textTransform="uppercase" mb={1.5}>
                                            Update Status
                                        </Typography>
                                        <Box display="flex" gap={1} flexWrap="wrap">
                                            {STATUS_OPTIONS.map((s) => {
                                                const st = getStatus(s);
                                                const isCurrent = selectedVisit.status === s;
                                                return (
                                                    <Button key={s} size="small"
                                                        disabled={isCurrent || updatingStatus}
                                                        onClick={() => handleStatusUpdate(selectedVisit.id, s)}
                                                        sx={{
                                                            borderRadius: "8px",
                                                            textTransform: "none",
                                                            fontSize: "12px", fontWeight: 600,
                                                            px: 1.5, py: 0.5,
                                                            backgroundColor: isCurrent ? st.bg : "transparent",
                                                            color: isCurrent ? st.color : "#aaa",
                                                            border: `1.5px solid ${isCurrent ? st.dot + "60" : "#e0e0e0"}`,
                                                            "&:hover": {
                                                                backgroundColor: st.bg,
                                                                color: st.color,
                                                                borderColor: st.dot,
                                                            },
                                                            "&.Mui-disabled": {
                                                                backgroundColor: st.bg,
                                                                color: st.color,
                                                            },
                                                        }}
                                                    >
                                                        {isCurrent ? `✓ ${s}` : s}
                                                    </Button>
                                                );
                                            })}
                                        </Box>
                                    </Box> */}
                                </Box>

                                {/* Footer */}
                                <Box sx={{
                                    p: 3, pt: 1, borderTop: "1px solid #f0f0f0",
                                    display: "flex", justifyContent: "flex-end",
                                }}>
                                    <Button onClick={() => setSelectedVisit(null)}
                                        variant="outlined" size="medium"
                                        sx={{
                                            borderRadius: "10px", textTransform: "none",
                                            fontWeight: 600, borderColor: "#e0e0e0", color: "#666",
                                            "&:hover": {
                                                borderColor: "#bbb", backgroundColor: "#fafafa",
                                            },
                                        }}>
                                        Close
                                    </Button>
                                </Box>
                            </>
                        )}
                    </Box>
                </Fade>
            </Modal>

            {/* ── Snackbar ── */}
            <Snackbar
                open={snackbar.open} autoHideDuration={3000}
                onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity={snackbar.severity} variant="filled"
                    sx={{ borderRadius: "10px", fontWeight: 600, fontSize: "13px" }}
                    onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default VendorSiteVisits;